import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $userAccountsStore, $userCardsStore } from '@lib/store';
import { cn, PaletteUtils, PermissionUtils } from '@shared/utils';
import type { UserResponse } from '@shared/api';
import { RemoveCardsModal } from '../modals';
import { AccountTileSmall, AddCardsModal, Cards, View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface AccountsCardsProps {
  className?: string;
  user: UserResponse;
}

export const AccountsAndCards = (props: AccountsCardsProps) => {
  const userAccountsStore = useUnit($userAccountsStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  const [showAddCards, setShowAddCards] = useState<boolean>(false);
  const [showRemoveCards, setShowRemoveCards] = useState<boolean>(false);

  const { t } = useTranslation('user');

  useEffect(() => {
    Promise.allSettled([
      $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id }),
      $userCardsStore.events.getUserCards({ AssignedUserId: props.user.id }),
    ]);
    return () => {
      $userAccountsStore.events.clear();
      $userCardsStore.events.clear();
    };
  }, [props.user.id]);

  const canManageAccounts = PermissionUtils.manageAccountsPermissions();
  const canManageCards = PermissionUtils.manageCardsPermissions();
  return (
    <section className={cn('flex flex-col gap-6', props.className)}>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <Text weight='medium' size='2xl'>
          Payments Accounts ({userAccountsStore.data.length})
        </Text>
        {(canManageAccounts || canManageCards) && (
          <div className='flex items-center gap-3'>
            <Button color='outlined' onClick={() => setShowAddCards(true)}>
              {t('buttons.add', { ns: 'common' })}
            </Button>
            <Button
              color='outlined'
              disabled={!userAccountsStore.data.length && !userCardsStore.data.data.length}
              onClick={() => setShowRemoveCards(true)}
            >
              {t('buttons.remove', { ns: 'common' })}
            </Button>
          </div>
        )}
      </div>
      <View
        loading={userAccountsStore.loading}
        noData={!userAccountsStore.data.length}
        noDataText={t('noData.noRecords', { ns: 'common' })}
        error={userAccountsStore.error}
        onRefresh={() => $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id })}
      >
        <div className={cn('grid grid-cols-2 gap-6', 'max-[1400px]:grid-cols-1', props.user.locked && 'pointer-events-none select-none opacity-40')}>
          {userAccountsStore.data.map((account) => (
            <AccountTileSmall key={account.id} account={account} />
          ))}
        </div>
      </View>
      <Cards
        title={
          <Text weight='medium' size='2xl'>
            {t('cards.title', { count: userCardsStore.data.data.length })}
          </Text>
        }
        className={cn(PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black')}
        userId={props.user.id}
      />
      <AddCardsModal
        isOpen={showAddCards}
        onClose={() => setShowAddCards(false)}
        user={props.user}
        accountsItems={userAccountsStore.data}
        cardsItems={userCardsStore.data.data}
        onSubmit={async (data) =>
          await Promise.all([
            data.accountIds.length > 0 && $userAccountsStore.events.addAccounts(data),
            data.cardIds.length > 0 && $userCardsStore.events.addCards(data),
          ])
        }
      />
      <RemoveCardsModal isOpen={showRemoveCards} user={props.user} onClose={() => setShowRemoveCards(false)} />
    </section>
  );
};
